/**
 * --------------------------------------------------------------------------
 * CartQuantityInput Web Component
 * --------------------------------------------------------------------------
 * A quantity input that automatically syncs with the optimistic cart state.
 * Shows the current quantity of a specific variant in the cart and updates
 * when the cart changes. Works with the optimistic cart system.
 */
if (!customElements.get("cart-quantity-input")) {
  class CartQuantityInput extends HTMLElement {
    static CONSTANTS = {
      INPUT_SELECTOR: 'input[type="number"]',
      DEFAULT_QUANTITY: 0,
      DEBOUNCE_DELAY: 300, // ms
    };

    constructor() {
      super();
      this.input = null;
      this.variantId = null;
      this.isInitialized = false;
      this.debounceTimer = null;
      this.lastCartQuantity = null;
      this.optimisticCartEnabled = true; // Default to enabled
      this.formId = null;
      this.formQuantityInput = null;
    }

    connectedCallback() {
      if (this.initialize()) {
        // Check if optimistic cart is enabled via data attribute
        const optimisticCartAttr = this.dataset.optimisticCart;
        this.optimisticCartEnabled = optimisticCartAttr !== 'false' && optimisticCartAttr !== false;
        
        // Only set up cart sync if optimistic cart is enabled
        if (this.optimisticCartEnabled) {
          // Validate that we have a variant ID when optimistic cart is enabled
          if (!this.variantId) {
            console.error(
              "CartQuantityInput Error: No variant ID found in data-variant-id attribute. Required when optimistic cart is enabled.",
              this,
            );
            return;
          }
          
          this.setupCartListener();
          this.setupInputListener();
          this.loadInitialQuantity();
        }
        
        // Always set up button listeners for basic functionality
        this.setupButtonListeners();
      }
    }

    disconnectedCallback() {
      this.removeCartListener();
      this.removeInputListener();
      this.clearDebounceTimer();
    }

    // --------------------------------------------------------------------
    // INITIALIZATION & SETUP
    // --------------------------------------------------------------------

    /**
     * Initializes the component by finding the required input element.
     * @returns {boolean} True if initialization is successful, false otherwise.
     */
    initialize() {
      this.input = this.querySelector(
        CartQuantityInput.CONSTANTS.INPUT_SELECTOR,
      );
      if (!this.input) {
        console.error(
          "CartQuantityInput Error: No number input found within the component.",
          this,
        );
        return false;
      }

      // Get variant ID from data attribute (only required if optimistic cart will be enabled)
      // We check in connectedCallback whether optimistic cart is enabled
      this.variantId = parseInt(this.input.dataset.variantId);
      
      // Get form ID from data attribute if provided
      this.formId = this.dataset.formId;
      if (this.formId) {
        this.setupFormSync();
      }
      
      // Note: variantId validation is deferred to connectedCallback based on optimisticCartEnabled

      this.isInitialized = true;
      return true;
    }

    /**
     * Sets up the cart update listener to sync quantity changes.
     */
    setupCartListener() {
      this.cartUpdateHandler = this.handleCartUpdate.bind(this);
      window.addEventListener("cart:updated", this.cartUpdateHandler);
      
      // Listen for cart processing state changes
      this.processingStateHandler = this.handleProcessingStateChange.bind(this);
      window.addEventListener("cart:processing-state-changed", this.processingStateHandler);
    }

    /**
     * Removes the cart update listener.
     */
    removeCartListener() {
      if (this.cartUpdateHandler) {
        window.removeEventListener("cart:updated", this.cartUpdateHandler);
        this.cartUpdateHandler = null;
      }
      if (this.processingStateHandler) {
        window.removeEventListener("cart:processing-state-changed", this.processingStateHandler);
        this.processingStateHandler = null;
      }
    }

    /**
     * Sets up the input change listener to update the cart.
     */
    setupInputListener() {
      this.inputChangeHandler = this.handleInputChange.bind(this);
      this.input.addEventListener("change", this.inputChangeHandler);
      this.input.addEventListener("input", this.handleInputChange.bind(this));
    }

    /**
     * Removes the input change listener.
     */
    removeInputListener() {
      if (this.inputChangeHandler && this.input) {
        this.input.removeEventListener("change", this.inputChangeHandler);
        this.input.removeEventListener("input", this.inputChangeHandler);
        this.inputChangeHandler = null;
      }
    }

    /**
     * Clears the debounce timer.
     */
    clearDebounceTimer() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
    }

    // --------------------------------------------------------------------
    // CART SYNC
    // --------------------------------------------------------------------

    /**
     * Loads the initial quantity from the cart state.
     */
    async loadInitialQuantity() {
      if (!window.Shopify || !window.Shopify.cart) return;

      try {
        // Ensure the cart is properly initialized first
        await window.Shopify.cart.ensureInitialized();
        const cart = await window.Shopify.cart.get();
        this.updateQuantity(cart);
        
        // Check initial processing state and update button states
        const isProcessing = window.Shopify.cart.isProcessing();
        this.updateButtonState(isProcessing);
      } catch (error) {
        console.warn(
          "CartQuantityInput: Failed to load initial cart state:",
          error,
        );
      }
    }

    /**
     * Handles cart update events and updates the input quantity.
     * @param {CustomEvent} event - The cart:updated event.
     */
    handleCartUpdate(event) {
      const { cart } = event.detail;
      this.updateQuantity(cart);
    }

    /**
     * Handles cart processing state change events.
     * @param {CustomEvent} event - The cart:processing-state-changed event.
     */
    handleProcessingStateChange(event) {
      const { isProcessing } = event.detail;
      this.updateButtonState(isProcessing);
    }

    /**
     * Sets up synchronization with the target form's quantity input.
     */
    setupFormSync() {
      if (!this.formId) return;
      
      const form = document.getElementById(this.formId);
      if (!form) {
        console.warn(
          `CartQuantityInput: Form with ID "${this.formId}" not found. Form syncing disabled.`,
          this,
        );
        return;
      }
      
      // Find the quantity input in the form (by name="quantity" or id ending with "-quantity")
      this.formQuantityInput = form.querySelector('input[name="quantity"]') || 
                               form.querySelector(`input[id$="-quantity"]`) ||
                               form.querySelector(`#${this.formId}-quantity`);
      
      if (!this.formQuantityInput) {
        console.warn(
          `CartQuantityInput: Quantity input not found in form "${this.formId}". Form syncing disabled.`,
          this,
        );
        return;
      }
    }

    /**
     * Updates the form's quantity input if form syncing is enabled.
     * @param {number} quantity - The quantity value to set in the form.
     */
    updateFormQuantity(quantity) {
      if (!this.formQuantityInput) return;
      
      // Update the form's quantity input
      if (this.formQuantityInput.value !== quantity.toString()) {
        this.formQuantityInput.value = quantity;
        // Dispatch change event to notify any listeners
        this.formQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /**
     * Updates the input quantity based on the cart state.
     * @param {Object} cart - The cart object from the optimistic cart system.
     */
    updateQuantity(cart) {
      if (!cart || !cart.items || !Array.isArray(cart.items)) return;

      // Find the item in the cart that matches this variant
      const item = cart.items.find(
        (i) => i.variant_id === this.variantId || i.id === this.variantId,
      );

      const quantity = item
        ? item.quantity
        : CartQuantityInput.CONSTANTS.DEFAULT_QUANTITY;
      this.lastCartQuantity = quantity;

      // Only update if the value has actually changed to avoid unnecessary DOM updates
      if (this.input.value !== quantity.toString()) {
        this.input.value = quantity;
        // Also update the form quantity if form syncing is enabled
        this.updateFormQuantity(quantity);
      }
    }

    // --------------------------------------------------------------------
    // INPUT HANDLING
    // --------------------------------------------------------------------

    /**
     * Handles input change events and updates the cart.
     * @param {Event} event - The input change event.
     */
    handleInputChange(event) {
      const newQuantity =
        parseInt(this.input.value) ||
        CartQuantityInput.CONSTANTS.DEFAULT_QUANTITY;

      // Always update form quantity when input changes (regardless of optimistic cart setting)
      this.updateFormQuantity(newQuantity);
      
      // Skip cart updates if optimistic cart is disabled
      if (!this.optimisticCartEnabled) {
        return;
      }
      
      // Don't allow changes if cart is processing
      if (window.Shopify?.cart?.isProcessing()) {
        return;
      }

      // Don't update if the quantity hasn't actually changed
      if (newQuantity === this.lastCartQuantity) return;

      // Clear any existing debounce timer
      this.clearDebounceTimer();

      // Debounce the cart update to avoid excessive API calls
      this.debounceTimer = setTimeout(() => {
        this.updateCartQuantity(newQuantity);
      }, CartQuantityInput.CONSTANTS.DEBOUNCE_DELAY);
    }

    /**
     * Updates the cart with the new quantity.
     * @param {number} quantity - The new quantity to set in the cart.
     */
    async updateCartQuantity(quantity) {
      if (!window.Shopify || !window.Shopify.cart) return;

      try {
        if (quantity === 0) {
          // Remove the item from cart
          await window.Shopify.cart.remove(this.variantId);
        } else {
          // Let the optimistic cart handle capacity checking and modal triggering
          // The cart.change() method will automatically cap quantities and emit events
          await window.Shopify.cart.change({
            id: this.variantId,
            quantity: quantity,
          });
          
          // If the quantity was capped by the cart, update the input to reflect the actual quantity
          const cart = await window.Shopify.cart.get();
          const currentItem = cart.items.find(
            (i) => i.variant_id === this.variantId || i.id === this.variantId
          );
          
          if (currentItem && currentItem.quantity !== quantity) {
            // Quantity was capped - update input to match actual cart quantity
            this.input.value = currentItem.quantity;
          }
        }
      } catch (error) {
        console.error(
          "CartQuantityInput: Failed to update cart quantity:",
          error,
        );
        // Revert the input to the last known cart quantity
        this.input.value =
          this.lastCartQuantity || CartQuantityInput.CONSTANTS.DEFAULT_QUANTITY;
      }
    }

    // --------------------------------------------------------------------
    // PUBLIC API
    // --------------------------------------------------------------------

    /**
     * Gets the current quantity value.
     * @returns {number} The current quantity.
     */
    getQuantity() {
      return (
        parseInt(this.input.value) ||
        CartQuantityInput.CONSTANTS.DEFAULT_QUANTITY
      );
    }

    /**
     * Sets the quantity value (useful for programmatic updates).
     * @param {number} quantity - The quantity to set.
     */
    setQuantity(quantity) {
      const newQuantity =
        parseInt(quantity) || CartQuantityInput.CONSTANTS.DEFAULT_QUANTITY;
      this.input.value = newQuantity;
      // Also update the form quantity if form syncing is enabled
      this.updateFormQuantity(newQuantity);
    }

    /**
     * Gets the variant ID associated with this input.
     * @returns {number} The variant ID.
     */
    getVariantId() {
      return this.variantId;
    }

    /**
     * Forces a refresh of the quantity from the current cart state.
     * Useful when you need to ensure the input is in sync with the cart.
     */
    async refreshFromCart() {
      if (!window.Shopify || !window.Shopify.cart) return;

      try {
        const cart = await window.Shopify.cart.get();
        this.updateQuantity(cart);
      } catch (error) {
        console.warn("CartQuantityInput: Failed to refresh from cart:", error);
      }
    }

    /**
     * Updates the max quantity for this input.
     * Called by the product form variant watcher when variants change.
     * @param {number} maxQuantity - The new maximum quantity value.
     */
    updateMaxQuantity(maxQuantity) {
      if (!this.input || isNaN(maxQuantity) || maxQuantity < 0) return;
      
      const currentMax = parseInt(this.input.getAttribute('max'));
      if (currentMax !== maxQuantity) {
        this.input.setAttribute('max', maxQuantity);
        
        // If current value exceeds new max, adjust it
        const currentValue = parseInt(this.input.value);
        if (currentValue > maxQuantity) {
          this.input.value = maxQuantity;
          // Trigger change event for any listeners
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    setupButtonListeners() {
      this.buttons = this.querySelectorAll("button");
      this.buttons.forEach((button) => {
        button.addEventListener(
          "click",
          this.handleButtonClick.bind(this),
          false,
        );
      });
    }

    handleButtonClick(event) {
      event.preventDefault();
      event.stopPropagation();
      
      // Don't allow clicks if cart is processing (only relevant when optimistic cart is enabled)
      if (this.optimisticCartEnabled && window.Shopify?.cart?.isProcessing()) {
        return;
      }
      
      const button = event.currentTarget;
      const value = parseInt(button.dataset.value);
      if (value > 0) {
        this.input.stepUp();
      } else {
        this.input.stepDown();
      }
      
      // Update form quantity immediately when buttons are clicked
      const newQuantity = parseInt(this.input.value) || CartQuantityInput.CONSTANTS.DEFAULT_QUANTITY;
      this.updateFormQuantity(newQuantity);
      
      // Only dispatch change event if optimistic cart is enabled
      // When disabled, the input is just for display/manual form submission
      if (this.optimisticCartEnabled) {
        this.input.dispatchEvent(new Event("change"));
      }
    }

    /**
     * Updates the state of the quantity input's buttons based on processing state.
     * @param {boolean} isProcessing - True if the cart is currently processing, false otherwise.
     */
    updateButtonState(isProcessing) {
      // Check if this specific variant is being processed
      const variantProcessing = window.Shopify?.cart?.isVariantProcessing?.(this.variantId) || false;
      const shouldDisable = isProcessing || variantProcessing;
      
      this.buttons.forEach((button) => {
        button.disabled = shouldDisable;
        
        // Add visual feedback for disabled state
        if (shouldDisable) {
          button.classList.add('opacity-50', 'cursor-not-allowed');
        } else {
          button.classList.remove('opacity-50', 'cursor-not-allowed');
        }
      });
    }
  }

  customElements.define("cart-quantity-input", CartQuantityInput);
}
